import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react"
import { ChevronDown } from "lucide-react"
import {
  ComponentPropsWithoutRef,
  ReactNode,
  RefObject,
  useId,
  useRef,
} from "react"

import { classNames } from "../../../../lib/classNames"
import {
  HEADLESS_OPTIONS_LIMIT,
  isHeadlessOptionsTruncated,
  renderHeadlessSafeItems,
} from "../../../../lib/headless"
import { Icon, IconSizes } from "../../../Icon"
import { KeyboardTile } from "../../informational/KeyboardTile"
import { Label } from "../../informational/Label"

export type SelectMenuProps<T> = {
  label?: string
  secondaryLabel?: string
  items: T[]
  selectedItem: T | null
  onSelectItem: ((item: T) => void) | ((item: T | null) => void)
  keyExtractor: (item: T) => string
  valueExtractor: (item: T) => string
  invalid?: boolean
  errorMessage?: string
  disabled?: boolean
  placeholder?: string
  isDeselectable?: boolean
  deselectText?: string
  shouldUsePlaceholderForDeselect?: boolean
  height?: number
  renderItem?: (item: T) => ReactNode
  renderSelectedItem?: (item: T | null) => ReactNode
  keyboardShortcut?: string
  autoFocus?: boolean
  buttonRef?: RefObject<HTMLButtonElement | null>
  dataTestId?: string
  eq?: (a: T | null, b: T | null) => boolean
  optionsClassName?: string
  buttonClassName?: string
  buttonAriaLabel?: string
  listBoxOptionsAnchor?: ComponentPropsWithoutRef<
    typeof ListboxOptions
  >["anchor"]
} & ComponentPropsWithoutRef<"div">

export function SelectMenu<T>(props: SelectMenuProps<T>) {
  const {
    eq,
    label,
    secondaryLabel,
    items,
    selectedItem,
    onSelectItem,
    keyExtractor,
    valueExtractor,
    invalid = false,
    errorMessage,
    disabled = false,
    placeholder,
    isDeselectable,
    shouldUsePlaceholderForDeselect = false,
    deselectText,
    height,
    renderItem,
    renderSelectedItem,
    keyboardShortcut,
    autoFocus,
    buttonRef,
    dataTestId,
    optionsClassName,
    buttonClassName,
    buttonAriaLabel,
    listBoxOptionsAnchor,
    ...otherProps
  } = props

  const innerRef = useRef<HTMLButtonElement>(null)
  const inputRef = buttonRef ?? innerRef

  const buttonId = useId()

  const handleChange = (item: T | null) => {
    if (isDeselectable) {
      return (onSelectItem as (item: T | null) => void)(item)
    }

    if (item === null || item === undefined) {
      throw new Error("Null select when not allowed")
    }

    return onSelectItem(item)
  }

  const getPlaceholder = () => {
    if (selectedItem) {
      return valueExtractor(selectedItem)
    }
    if (shouldUsePlaceholderForDeselect) {
      return placeholder
    }
    if (isDeselectable) {
      return deselectText ?? "None"
    }
    return placeholder ?? "Select one"
  }

  const defaultOptionClassName = "relative cursor-default select-none px-4 py-2"

  return (
    <div {...otherProps}>
      <Listbox
        by={eq}
        value={selectedItem}
        onChange={handleChange}
        disabled={disabled}
      >
        <Label
          label={label}
          secondaryLabel={secondaryLabel}
          errorMessage={errorMessage}
          inputId={buttonId}
        >
          <div className="relative">
            <ListboxButton
              id={buttonId}
              className={classNames(
                "bg-v2-background-elevation-raised border-v2-border-default shadow-control rounded-control group relative w-full cursor-default border py-2 pl-3 pr-10 text-left",
                "hover:not-disabled:border-v2-border-strong",
                "disabled:border-v2-border-default-disabled disabled:bg-v2-background-surface-tonal disabled:text-v2-foreground-default-disabled",
                (invalid || !!errorMessage) &&
                  "border-v2-border-danger hover:not-disabled:border-v2-border-danger",
                !!height && `h-${height}`,
                buttonClassName
              )}
              ref={inputRef}
              autoFocus={autoFocus}
              data-testid={dataTestId}
              aria-keyshortcuts={keyboardShortcut?.toUpperCase()}
              aria-label={buttonAriaLabel}
            >
              {renderSelectedItem ? (
                renderSelectedItem(selectedItem)
              ) : (
                <span
                  className={classNames(
                    "text-body group-disabled:text-v2-foreground-default-disabled block truncate",
                    selectedItem
                      ? "color-default"
                      : "text-v2-foreground-default-weaker"
                  )}
                >
                  {getPlaceholder()}
                </span>
              )}
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
                <Icon
                  icon={ChevronDown}
                  size={IconSizes.ExtraSmall}
                  className="text-v2-foreground-default-weak group-disabled:text-v2-foreground-default-disabled"
                  aria-hidden="true"
                />
              </span>
              {keyboardShortcut && (
                <KeyboardTile
                  color="gray"
                  keyboardKey={keyboardShortcut?.toUpperCase()}
                  className="absolute inset-y-2 right-8 hidden group-hover:flex"
                />
              )}
            </ListboxButton>

            <ListboxOptions
              className={classNames(
                "bg-v2-background-elevation-overlay border-v2-border-default shadow-v2-overlay text-body rounded-overlay z-10 mt-2 max-h-60 overflow-auto border focus-visible:ring-0",
                !listBoxOptionsAnchor && "absolute w-full",
                optionsClassName
              )}
              modal={false}
              anchor={listBoxOptionsAnchor}
            >
              {isDeselectable && (
                <ListboxOption
                  className={({ selected, focus }) =>
                    classNames(
                      defaultOptionClassName,
                      focus && "bg-v2-background-surface-strong",
                      selected && "bg-v2-background-active-hover"
                    )
                  }
                  value={null}
                >
                  {({ selected }) => (
                    <div className="flex flex-row items-center gap-x-1">
                      <span
                        className={classNames(
                          "color-subtle block truncate",
                          selected && "color-default",
                          selected && "text-body-strong"
                        )}
                      >
                        {deselectText ?? "None"}
                      </span>
                    </div>
                  )}
                </ListboxOption>
              )}
              {!!items &&
                renderHeadlessSafeItems(items).map((item) => (
                  <ListboxOption
                    key={keyExtractor(item)}
                    className={({ selected, focus }) =>
                      classNames(
                        defaultOptionClassName,
                        focus && "bg-v2-background-surface-strong",
                        selected && "bg-v2-background-active-hover"
                      )
                    }
                    value={item}
                  >
                    {({ selected }) => (
                      <div className="flex flex-row items-center gap-x-1">
                        {renderItem ? (
                          renderItem(item)
                        ) : (
                          <span
                            className={classNames(
                              "color-subtle block truncate",
                              selected && "color-default",
                              selected && "text-body-strong"
                            )}
                          >
                            {valueExtractor(item)}
                          </span>
                        )}
                      </div>
                    )}
                  </ListboxOption>
                ))}
              {!!items && isHeadlessOptionsTruncated(items) && (
                <div
                  className={classNames(
                    defaultOptionClassName,
                    "text-body-sm color-subtle text-center"
                  )}
                >
                  Showing the first {HEADLESS_OPTIONS_LIMIT.toLocaleString()}{" "}
                  options.
                </div>
              )}
            </ListboxOptions>
          </div>
        </Label>
      </Listbox>
    </div>
  )
}
